/**
 * ScrPanel.tsx — the SCR slot at the top of the clinician review, one render per state.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: returning patients S7.1 (2026-09-25).
 *
 * WHY ITS OWN FILE. ReviewView used to hold this as a three-way ternary whose
 * only failure branch was an error with Try again. The states are now typed
 * (scrPanelState.ts), and each one decides its own look:
 *
 *  - no account yet / email not confirmed: a plain card with the heading and
 *    one sentence of the CTO's copy. NO error tone, NO icon, NO role="alert"
 *    and NO Try again. It is a normal situation, and retrying cannot change
 *    it. The staff surfaces keep one assertive region, which is R31's.
 *  - no patient record: the same calm card with the existing line.
 *  - failed: the error, with Try again ONLY when a retry can succeed
 *    (a 5xx or a network failure).
 *
 * Money never appears here (this is a clinician surface).
 */
import type { AssessmentPatientIdentity } from '../api/types';
import { SectionTitle } from '../staff/ui';
import { Card, ErrorState, Help, Spinner } from '../ui/kit';
import { ScrQuickCopy } from './ScrQuickCopy';
import {
  SCR_COMPLETED_UNVERIFIED_COPY,
  SCR_FAILED_COPY,
  SCR_NOT_COMPLETED_COPY,
  SCR_NO_RECORD_COPY,
  type ScrPanelState,
} from './scrPanelState';

/** A calm, informational card: the panel heading and one line of plain copy. */
function ScrNote({ children }: { children: string }) {
  return (
    <Card>
      <SectionTitle>SCR quick-copy</SectionTitle>
      <Help style={{ marginBottom: 0 }}>{children}</Help>
    </Card>
  );
}

export function ScrPanel({
  state,
  onRetry,
  name,
  consent,
}: {
  state: ScrPanelState;
  /** Re-issue the SCR read. Offered only on a retryable failure. */
  onRetry: () => void;
  /** The Lead-resolved acquisition name (see ScrQuickCopy). */
  name?: AssessmentPatientIdentity | null;
  /** The SCR-check consent from the assessment review (undefined until it loads). */
  consent?: boolean | null;
}) {
  switch (state.kind) {
    case 'loading':
      return (
        <Card>
          <Spinner label="Loading SCR details…" />
        </Card>
      );
    case 'not_completed':
      return <ScrNote>{SCR_NOT_COMPLETED_COPY}</ScrNote>;
    case 'completed_unverified':
      return <ScrNote>{SCR_COMPLETED_UNVERIFIED_COPY}</ScrNote>;
    case 'skip':
    case 'no_record':
      return <ScrNote>{SCR_NO_RECORD_COPY}</ScrNote>;
    case 'failed':
      return (
        <Card>
          <ErrorState message={SCR_FAILED_COPY} onRetry={state.retryable ? onRetry : undefined} />
        </Card>
      );
    case 'ready':
      return <ScrQuickCopy scr={state.scr} name={name} consent={consent} />;
  }
}
